import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx } from '../cx';
import { formatNumber, type DsLocale } from '../format';

export type FilterChipProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'onToggle'> & {
  label: ReactNode;
  selected: boolean;
  onSelectedChange: (selected: boolean) => void;
} & (
    | { count?: undefined; locale?: DsLocale }
    | {
        /** Number of results for this value. */
        count: number;
        /** The page language, required with a count so it groups the way that language does. */
        locale: DsLocale;
      }
  );

/**
 * A tappable value in a filter list: the label with its count, filled with the selected colour when
 * on. No checkmarks or checkboxes. At least 40px tall; the whole chip is the target. Multi-select
 * lists wrap.
 */
export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(function FilterChip(
  { label, count, locale = 'en', selected, onSelectedChange, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      aria-pressed={selected}
      onClick={() => onSelectedChange(!selected)}
      className={cx(
        'inline-flex h-10 items-center gap-1.5 rounded-jc-control px-4 text-sm whitespace-nowrap',
        'transition-colors duration-(--jc-duration-fast) ease-jc-out motion-reduce:transition-none',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus focus-visible:ring-offset-2 focus-visible:ring-offset-jc-page',
        'disabled:cursor-not-allowed disabled:opacity-50',
        selected
          ? 'bg-jc-selected font-semibold text-jc-on-selected'
          : 'bg-jc-surface font-semibold text-jc-ink ring-1 ring-inset ring-jc-border-control hover:bg-jc-elevated',
        className,
      )}
    >
      <span>{label}</span>
      {count !== undefined ? <span className="tabular-nums opacity-75">{formatNumber(count, locale)}</span> : null}
    </button>
  );
});
